/**
 * Shared classifier for auth/credential errors that a token refresh + retry can
 * recover from — stale Cognito Identity-Pool credentials, expired/invalid
 * tokens, "No current user" (`NoSignedUser`), etc.
 *
 * Deliberately does NOT match Cognito throttling
 * (`TooManyRequestsException` / "rate exceeded"): a throttle is not an
 * expired/invalid token, and force-refreshing in response only hammers Cognito
 * harder and self-amplifies into the credential-refresh storm (see PR #7299).
 * Throttled calls back off on their own.
 *
 * Single source of truth consumed by the GraphQL client's refresh-and-retry
 * shell, the react-query retry policy, and `withAWSRetry`.
 */
export const isAuthCredentialError = (error: unknown): error is Error => {
  if (!(error instanceof Error)) {
    return false;
  }

  const name = error.name.toLowerCase();
  const message = error.message.toLowerCase();

  // Throttle is NOT recoverable by a token refresh — never trigger one.
  if (
    name.includes("toomanyrequests") ||
    message.includes("toomanyrequests") ||
    message.includes("rate exceeded")
  ) {
    return false;
  }

  // Name-based: Cognito / STS auth exception classes.
  if (
    name.includes("nosigneduser") ||
    name.includes("notauthorized") ||
    name.includes("expiredtoken")
  ) {
    return true;
  }

  return (
    message.includes("no current user") ||
    message.includes("no signed user") ||
    message.includes("nosigneduser") ||
    message.includes("unauthorized") ||
    // Token problems — require an auth qualifier so we don't false-positive on
    // unrelated "token"/"expired" nouns ("CSRF token missing", "slot expired").
    // Still covers "token expired", "invalid login token", and AWS's
    // "The security token included in the request is expired".
    (message.includes("token") &&
      (message.includes("expired") ||
        message.includes("invalid") ||
        message.includes("revoked")))
  );
};
